import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeEthiopianPhone, pinProblem, type UserDto } from '@dairy/shared';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { toast } from '@/components/toast';
import { Badge, Button, Card, Dialog, Field, Input, PageHeader } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { apiJson } from '@/lib/api';
import { useFormat } from '@/lib/format';
import { ReportState, useOnline } from '@/features/reports/common';

/** Screen 26 — Users (owner, PL-5): add a manager, reset a PIN, deactivate (with the warning) and reactivate. */
export function UsersPage() {
  const { t } = useTranslation();
  const f = useFormat();
  const online = useOnline();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['users'], queryFn: () => apiJson<UserDto[]>('/users'), enabled: online });
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState<UserDto | null>(null);
  const [deactivating, setDeactivating] = useState<UserDto | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['users'] });
  const setActive = async (u: UserDto, active: boolean) => {
    try {
      await apiJson(`/users/${u.id}/${active ? 'activate' : 'deactivate'}`, { method: 'POST' });
      toast(t('common.saved'));
      refresh();
    } catch (e) {
      toast(errorMessage(t, e), 'error');
    }
  };
  return (
    <div className="mx-auto max-w-lg space-y-4">
      <PageHeader
        title={t('settings.users')}
        actions={
          <Button size="sm" onClick={() => setAdding(true)} disabled={!online}>
            <Plus className="h-4 w-4" /> {t('users.addManager')}
          </Button>
        }
      />
      <ReportState online={online} isLoading={q.isLoading} error={q.error}>
        <ul className="space-y-2">
          {(q.data ?? []).map((u) => (
            <li key={u.id}>
              <Card className="space-y-2 p-3">
                <div className="flex items-center gap-2">
                  <span className="flex-1 font-semibold">{u.name}</span>
                  <Badge tone={u.role === 'OWNER' ? 'purple' : 'blue'}>{t(`users.role.${u.role}`)}</Badge>
                  {!u.active ? <Badge tone="red">{t('common.inactive')}</Badge> : null}
                </div>
                <div className="text-sm text-stone-600">{f.phone(u.phone)}</div>
                {u.role === 'MANAGER' ? (
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setResetting(u)}>
                      {t('users.resetPin')}
                    </Button>
                    {u.active ? (
                      <Button size="sm" variant="ghost" onClick={() => setDeactivating(u)}>
                        {t('users.deactivate')}
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => void setActive(u, true)}>
                        {t('users.activate')}
                      </Button>
                    )}
                  </div>
                ) : null}
              </Card>
            </li>
          ))}
        </ul>
      </ReportState>
      {adding ? <AddManagerDialog onClose={() => setAdding(false)} onDone={refresh} /> : null}
      {resetting ? <ResetPinDialog user={resetting} onClose={() => setResetting(null)} /> : null}
      <ConfirmDialog
        open={!!deactivating}
        onOpenChange={(o) => !o && setDeactivating(null)}
        title={t('users.deactivateTitle', { name: deactivating?.name })}
        danger
        confirmLabel={t('users.deactivate')}
        onConfirm={() => (deactivating ? setActive(deactivating, false) : undefined)}
      >
        <p>{t('users.deactivateWarning')}</p>
      </ConfirmDialog>
    </div>
  );
}

function AddManagerDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (!name.trim()) return setError(t('users.errors.name'));
    if (!normalizeEthiopianPhone(phone)) return setError(t('users.errors.phone'));
    if (pinProblem(pin)) return setError(t(`users.errors.pin${pinProblem(pin)}`));
    try {
      await apiJson('/users', { method: 'POST', json: { name: name.trim(), phone, pin } });
      toast(t('users.managerAdded'));
      onDone();
      onClose();
    } catch (e) {
      setError(errorMessage(t, e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={t('users.addManager')} footer={<Button onClick={() => void submit()}>{t('common.save')}</Button>}>
      <div className="space-y-3">
        <Field label={t('users.name')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
        </Field>
        <Field label={t('login.phone')}>
          <Input inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="09XXXXXXXX" />
        </Field>
        <Field label={t('users.firstPin')} hint={t('users.pinHint')}>
          <Input inputMode="numeric" value={pin} maxLength={6} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} />
        </Field>
        {error ? <p className="text-red-700">{error}</p> : null}
      </div>
    </Dialog>
  );
}

function ResetPinDialog({ user, onClose }: { user: UserDto; onClose: () => void }) {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (pinProblem(pin)) return setError(t(`users.errors.pin${pinProblem(pin)}`));
    try {
      await apiJson(`/users/${user.id}/reset-pin`, { method: 'POST', json: { pin } });
      toast(t('users.pinReset'));
      onClose();
    } catch (e) {
      setError(errorMessage(t, e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={t('users.resetPinTitle', { name: user.name })} footer={<Button onClick={() => void submit()}>{t('common.save')}</Button>}>
      <p className="mb-3 text-stone-700">{t('users.resetPinHelp')}</p>
      <Field label={t('users.newPin')} hint={t('users.pinHint')}>
        <Input inputMode="numeric" value={pin} maxLength={6} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} />
      </Field>
      {error ? <p className="mt-2 text-red-700">{error}</p> : null}
    </Dialog>
  );
}
